import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { PostListingData } from 'src/app/types/post';
import { environment } from 'src/environments/environment';
import { CategoryItem } from 'src/app/types/category';
import { CinemaProvider, Movie } from 'src/app/types/movie';
import { Genre, GlobalDataService } from 'src/app/services/global-data.service';
import { SeoService } from 'src/app/services/seo.service';
import { SelectListComponent } from 'src/app/components/select-list/select-list.component';
import { SEOSchema } from 'src/app/types/seo';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { generateUUID } from 'src/app/utils/string-transform';

@Component({
  selector: 'app-movies-listing',
  templateUrl: './movies-listing.page.html',
  styleUrls: ['./movies-listing.page.scss'],
})
export class MoviesListingPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  selectedCategory: CategoryItem | null = null;
  cinemaProvider: CinemaProvider;
  selectedSection: string = 'all_movies';
  selectedLabel: string = 'All Movies'; // Default label
  data: PostListingData | any = {
    title: 'Movies',
    excerpt: '',
  };
  items: Movie[];
  nowShowing: Movie[] = [];
  comingSoon: Movie[] = [];
  allMovies: Movie[] = [];
  nowShowingGenres: CategoryItem[] = [];
  comingSoonGenres: CategoryItem[] = [];

  sections = [
    { key: 'all_movies', label: 'All Movies' },
    { key: 'now_showing', label: 'Now Booking' },
    { key: 'coming_soon', label: 'Coming Soon' },
  ];

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  constructor(
    private platform: Platform,
    private seo: SeoService,
    private route: ActivatedRoute,
    public popoverController: PopoverController,
    private globalData: GlobalDataService,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });

    this.globalData.dataChange.subscribe((globalData) => {
      if (globalData?.nowShowing) {
        this.nowShowing = globalData.nowShowing;
        this.comingSoon = globalData.comingSoon;
        this.allMovies = [...globalData.nowShowing, ...globalData.comingSoon];
        this.nowShowingGenres = globalData.nowShowingGenres;
        this.comingSoonGenres = globalData.comingSoonGenres;
        this.setCinemaProvider(globalData.cinemaProvider);
      }
    });
  }

  /**
   * Track by function for results
   */
  trackByID(index: number, el: any): string {
    return el.uuid;
  }

  /**
   * Toggle between Now Booking & Coming Soon
   */
  toggleSelectedSection(section: string) {
    if (this.selectedSection === section) {
      return;
    }

    // Set the selected section and label
    this.selectedSection = section;
    const selected = this.sections.find((s) => s.key === section);
    this.selectedLabel = selected ? selected.label : 'All Categories';

    // Reset items and categories
    this.items = null;
    this.resetCategories();

    // Load items based on the selected section
    switch (section) {
      case 'all_movies':
        this.items = this.allMovies.map(item => ({
          ...item,
          uuid: generateUUID()
        }));
        break;

      case 'now_showing':
        this.items = this.nowShowing.map(item => ({
          ...item,
          uuid: generateUUID()
        }));
        break;

      case 'coming_soon':
        this.items = this.comingSoon.map(item => ({
          ...item,
          uuid: generateUUID()
        }));
        break;

      default:
        console.error(`Unknown section: ${section}`);
    }
  }

  /**
   * Set provider according to global data
   */
  setCinemaProvider(provider) {
    this.cinemaProvider = provider;
    this.data.title =
      environment.environment_name == 'rosebank'
        ? 'Cinema Nouveau Movies'
        : provider.name + ' Movies';

    if (this.cinemaProvider.slug === 'numetro') {
      if (environment.environment_name == 'canalwalk') {
        this.data.excerpt =
          "Immerse yourself in the best cinema experience Cape Town has to offer with Nu Metro's new 4DX, Scene Xtreme, Scene VIP, Standard 3D and 2D cinemas, or host your little one's next party at their dedicated Kidz cinema. If you want to watch the latest blockbuster and feel like you're right there in the middle of the action, then Nu Metro's 4DX experience is for you! Taking the film experience to a whole new level, Nu Metro's 4DX experience will have you hanging onto the edge of your seat as you go on an adventure of a lifetime with your favourite characters. If you want to watch a much-anticipated movie release on the biggest screen in town, then you have to head to Nu Metro's Scene Xtreme. The screen is three storeys high, and the 360-degree sound will fully immerse you. If you're looking for the ultimate luxury and comfort, then book your tickets at Nu Metro's Scene VIP. It includes an exclusive VIP lounge and bar area, fully reclinable seating, and service at your seat.";
      } else if (environment.environment_name == 'woodlands') {
        this.data.excerpt =
          'Immerse yourself in the best cinema experience at Woodlands VIP cinemas. Book your tickets. We have an exclusive VIP lounge and bar area for your experience.';
      } else if (environment.environment_name == 'clearwater') {
        this.data.excerpt =
          'Clearwater Mall has the latest movies for you and your loved ones to watch in cinema. Come to Clearwater mall in Roodepoort to see the best in entertainment';
      } else if (environment.environment_name == 'glen') {
        this.data.excerpt =
          'Get your popcorn and snacks and enjoy the latest selection of movies at the cinema in The Glen Shopping Centre today!';
      } else {
        this.data.excerpt =
          "Immerse yourself in the best cinema experience at Nu Metro's Scene Xtreme and Scene VIP, or Standard 3D and 2D cinemas. If you want to watch a much-anticipated movie release on the biggest screen in town, then you have to head to Nu Metro's Scene Xtreme. If you're looking for the ultimate luxury and comfort, then book your tickets at Nu Metro's Scene VIP. It includes an exclusive VIP lounge and bar area, fully reclinable seating, and service at your seat.";
      }
    } else {
      if (environment.environment_name == 'capegate') {
        this.data.excerpt =
          'We have a variety of Ster-Kinekor moivies available for the whole family right here at Capegate shopping centre.';
      } else if (environment.environment_name == 'somerset') {
        this.data.excerpt =
          'Immerse yourself in the best cinema experience at Somerset Mall VIP cinemas. Book your tickets. We have an exclusive VIP lounge and bar area for your enjoyment.';
      } else if (environment.environment_name == 'rosebank') {
        this.data.excerpt =
          "Discover the latest blockbusters, coming soon titles, and special events at Rosebank Mall's Ster-Kinekor cinema. Enjoy a diverse movie experience, from new releases to throwback films.";
      } else {
        this.data.excerpt =
          "To most South Africans, the word “movies” is synonymous with the name “Ster-Kinekor”; the biggest movie exhibitor by far (more than double the size of it's nearest competitor), Ster-Kinekor owns some 60-65% of the market, boasting some 55 movie complexes in South Africa (more than 400 screens and 64 000 seats) with 154 state-of-the-art 3D cinemas, giving Ster-Kinekor the largest 3D footprint in this territory, as well as 6 complexes in neighbouring territories.";
      }
    }

    this.items = this.allMovies.map(item => ({
      ...item,
      uuid: generateUUID()
    }));
  }

  /**
   * Reset category filter
   */
  resetCategories() {
    if (this.selectedSection === 'now_showing') {
      this.items = this.nowShowing.map(item => ({
        ...item,
        uuid: generateUUID()
      }));
    } else {
      this.items = this.comingSoon.map(item => ({
        ...item,
        uuid: generateUUID()
      }));
    }

    this.selectedCategory = null;
  }

  /**
   * Present category select popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: SelectListComponent,
      componentProps: {
        data:
          this.selectedSection === 'now_showing'
            ? this.nowShowingGenres
            : this.comingSoonGenres,
      },
      event: ev,
      translucent: true,
    });

    popover.onWillDismiss().then((popoverData) => {
      if (popoverData.data === 'all') {
        this.resetCategories();
      } else {
        const filterCategory = {
          title: popoverData.data?.title,
          slug: popoverData.data?.slug,
        };
        if (popoverData.data && this.selectedCategory != filterCategory) {
          this.selectedCategory = filterCategory;

          if (this.selectedCategory?.slug) {
            const movies =
              this.selectedSection === 'now_showing'
                ? this.nowShowing
                : this.comingSoon;

            if (this.cinemaProvider.slug === 'numetro') {
              this.items = movies.filter((movie) => {
                return movie.genres?.includes(this.selectedCategory.title);
              });
            } else {
              this.items = movies.filter((movie) => {
                const matchedMovies = movie.genres?.filter((genre: Genre) => {
                  return genre.name === this.selectedCategory.title;
                });

                return matchedMovies.length > 0;
              });
            }

            this.selectedCategory = popoverData.data;
          }
        }
      }
    });

    return await popover.present();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();
    if (initialMallData?.nowShowing) {
      this.nowShowing = initialMallData.nowShowing;
      this.comingSoon = initialMallData.comingSoon;
      this.allMovies = [...initialMallData.nowShowing, ...initialMallData.comingSoon];
      this.nowShowingGenres = initialMallData.nowShowingGenres;
      this.comingSoonGenres = initialMallData.comingSoonGenres;
      this.setCinemaProvider(initialMallData.cinemaProvider);
    }

    this.route.data.subscribe((response: any) => {
      // Set Meta & Social tags
      if (!this.cordova) {
        let schemaData = { ...this.schema };

        // Title tag
        const seoTitle =
          initialMallData.cinemaProvider.name +
          ' Movies | ' +
          environment.environment_title +
          ' Shopping Centre';
        this.seo.setTitle(seoTitle);
        schemaData = {
          ...schemaData,
          name: seoTitle,
        };

        // Description tag
        const seoData = {
          title: seoTitle,
          description: this.data.excerpt,
          keywords:
            initialMallData.general.metaKeywords +
            ' movies ' +
            initialMallData.cinemaProvider.name,
          socialImage: initialMallData.social.openGraphImage,
        };
        schemaData = {
          ...schemaData,
          description: seoData.description,
        };
        this.seo.setMeta(seoData);
        this.seo.setSEOMeta(seoData);

        this.schema = { ...this.schema, ...schemaData };
        this.jsonLdService.insertSchema(this.schema);
      }
    });
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
